import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/hooks/useTheme';

interface ModalProps {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Plein écran bas (bottom sheet) ou hauteur selon le contenu */
  variant?: 'sheet' | 'auto';
}

export function Modal({ visible, onClose, children, variant = 'sheet' }: ModalProps) {
  const colors = useTheme();

  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [visible]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && visible) onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [visible, onClose]);

  if (!visible) return null;

  const contentStyle =
    variant === 'sheet'
      ? { ...styles.contentSheet, backgroundColor: colors.background }
      : { ...styles.contentAuto, backgroundColor: colors.background };

  return createPortal(
    <div style={styles.overlay} onClick={onClose}>
      <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body,
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    zIndex: 5000,
  },
  contentSheet: {
    width: '100%',
    maxWidth: 600,
    height: 'min(94dvh, 720px)',
    maxHeight: 'min(94dvh, 720px)',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp 0.3s ease-out',
    boxShadow: '0 -8px 32px rgba(0,0,0,0.2)',
  },
  contentAuto: {
    width: '100%',
    maxWidth: 600,
    maxHeight: 'min(90vh, 720px)',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp 0.3s ease-out',
    boxShadow: '0 -8px 32px rgba(0,0,0,0.2)',
  },
};
